import { useIntl } from "react-intl";
import { SimpleEntityGridColumn } from "../grid/SimpleEntityGrid";
import { KTSVG } from "../../../_metronic/helpers";
import { ICellRendererParams } from "ag-grid-enterprise";
import Avatar from "react-avatar";
import { includes } from "lodash";
import { ManagerStat } from "src/app/types/orders/order";

export default function useNLForLinkerGridDashboard(): Array<SimpleEntityGridColumn> {
    const { formatMessage } = useIntl();

    const cellRenderer = ({ data }: { data: ManagerStat }) => {
        return (
            <div className="d-flex">
                <a className="inline-action" href={`https://${data.domain}`} target="_blank">
                    <KTSVG path="/media/icons/duotune/general/gen004.svg" className="svg-icon-2" />
                </a>
                <a
                    className="inline-action"
                    href={`/analyse/zoom/content?moneySite=${data.id}`}
                    target="_blank"
                >
                    <KTSVG path="/media/icons/duotune/graphs/gra004.svg" className="svg-icon-2" />
                </a>
            </div>
        );
    };

    const gridColumns: Array<SimpleEntityGridColumn> = [
        {
            field: "actions",
            sortable: false,
            filter: false,
            pinned: "left",
            cellRenderer,
            cellRendererParams: ({ data }: ICellRendererParams) => ({ data }),
            minWidth: 85,
            maxWidth: 85,
        },
        {
            field: "manager",
            headerName: formatMessage({ id: "sites.grid.col.manager" }),
            autoHeight: true,
            pinned: "left",
            valueGetter: ({ data }: { data: ManagerStat }) => (data.manager ? data.manager : ""),
            cellRenderer: ({ data }: { data: ManagerStat }) => (
                <>
                    {data.manager && (
                        <div>
                            <Avatar
                                round
                                className={
                                    includes(data.managerRole, "LEAD") ? "avatar-is-Leader" : ""
                                }
                                size="24px"
                                fgColor={
                                    includes(data.managerRole, "LEAD")
                                        ? "var(--kt-primary-inverse)"
                                        : "var(--kt-secondary-inverse)"
                                }
                                color={
                                    includes(data.managerRole, "LEAD")
                                        ? "var(--kt-primary)"
                                        : "var(--kt-secondary)"
                                }
                                textSizeRatio={2}
                                name={data.manager}
                            />
                        </div>
                    )}
                </>
            ),
            filter: "agSetColumnFilter",
            minWidth: 60,
            maxWidth: 60,
        },
        {
            field: "linker",
            headerName: formatMessage({ id: "sites.grid.col.linker" }),
            autoHeight: true,
            pinned: "left",
            valueGetter: ({ data }: { data: ManagerStat }) => (data.linker ? data.linker : ""),
            cellRenderer: ({ data }: { data: ManagerStat }) => (
                <>
                    {data.linker && (
                        <div>
                            <Avatar
                                round
                                className={
                                    includes(data.linkerRole, "LEAD") ? "avatar-is-Leader" : ""
                                }
                                size="24px"
                                fgColor={
                                    includes(data.linkerRole, "LEAD")
                                        ? "var(--kt-primary-inverse)"
                                        : "var(--kt-secondary-inverse)"
                                }
                                color={
                                    includes(data.linkerRole, "LEAD")
                                        ? "var(--kt-primary)"
                                        : "var(--kt-secondary)"
                                }
                                textSizeRatio={2}
                                name={data.linker}
                            />
                        </div>
                    )}
                </>
            ),
            filter: "agSetColumnFilter",
            minWidth: 60,
            maxWidth: 60,
        },
        {
            field: "pbn",
            headerName: formatMessage({ id: "sites.grid.col.pbn" }),
            pinned: "left",
            minWidth: 65,
            maxWidth: 65,
        },
        {
            field: "domain",
            headerName: formatMessage({ id: "sites.grid.col.domain" }),
            pinned: "left",
            width: 300,
        },
        {
            field: "planned",
            headerName: formatMessage({ id: "sites.grid.col.planned" }),
            cellClass: "number-cell",
            valueGetter: ({ data }: { data: ManagerStat }) => data?.planned || 0,
            minWidth: 70,
            maxWidth: 70,
        },
        {
            headerName: formatMessage({ id: "sites.grid.col.previousMonth" }),
            children: [
                {
                    field: "previousMonthOrdered",
                    headerName: formatMessage({ id: "sites.grid.col.ordered" }),
                    valueGetter: ({ data }: { data: ManagerStat }) =>
                        data?.previousMonthOrdered || 0,
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: ManagerStat }) =>
                            data.previousMonthOrdered >= data.planned,
                    },
                    minWidth: 100,
                    maxWidth: 100,
                    cellStyle: { borderLeft: "1px solid var(--ag-border-color)" },
                },
                {
                    field: "previousMonthDelivered",
                    headerName: formatMessage({ id: "sites.grid.col.delivered" }),
                    valueGetter: ({ data }: { data: ManagerStat }) =>
                        data?.previousMonthDelivered || 0,
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: ManagerStat }) =>
                            data.previousMonthDelivered >= data.previousMonthOrdered &&
                            data.previousMonthDelivered >= data.planned,
                    },
                    minWidth: 70,
                    maxWidth: 70,
                },
                {
                    field: "currentMonthProcessed",
                    headerName: formatMessage({ id: "sites.grid.col.processed" }),
                    valueGetter: ({ data }: { data: ManagerStat }) =>
                        data?.currentMonthProcessed || 0,
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: any) =>
                            data.currentMonthProcessed >= data.previousMonthDelivered &&
                            data.currentMonthProcessed >= data.planned,
                    },
                    minWidth: 70,
                    maxWidth: 70,
                    cellStyle: { borderRight: "1px solid var(--ag-border-color)" },
                },
                {
                    field: "previousMonthWaiting",
                    headerName: formatMessage({ id: "sites.grid.col.waiting" }),
                    valueGetter: ({ data }: { data: ManagerStat }) =>
                        data?.previousMonthWaiting || 0,
                    cellClassRules: {
                        "order-success": ({ data }: any) =>
                            data.previousMonthWaiting >= data.currentMonthProcessed &&
                            data.previousMonthWaiting >= data.planned,
                    },
                    cellClass: "number-cell",
                    minWidth: 70,
                    maxWidth: 70,
                },
                {
                    field: "previousMonthPublished",
                    headerName: formatMessage({ id: "sites.grid.col.published" }),
                    valueGetter: ({ data }: { data: ManagerStat }) =>
                        data?.previousMonthPublished || 0,
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: ManagerStat }) =>
                            data.previousMonthPublished >= data.previousMonthOrdered &&
                            data.previousMonthPublished >= data.previousMonthDelivered &&
                            data.previousMonthPublished >= data.planned,
                    },
                    minWidth: 70,
                    maxWidth: 70,
                    cellStyle: { borderRight: "1px solid var(--ag-border-color)" },
                },
            ],
        },
        {
            headerName: formatMessage({ id: "sites.grid.col.currentMonth" }),
            children: [
                {
                    field: "currentMonthOrdered",
                    headerName: formatMessage({ id: "sites.grid.col.ordered" }),
                    valueGetter: ({ data }: { data: ManagerStat }) =>
                        data?.currentMonthOrdered || 0,
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: ManagerStat }) =>
                            data.currentMonthOrdered >= data.planned,
                    },
                    minWidth: 100,
                    maxWidth: 100,
                    cellStyle: { borderLeft: "1px solid var(--ag-border-color)" },
                },
                {
                    field: "currentMonthDelivered",
                    headerName: formatMessage({ id: "sites.grid.col.delivered" }),
                    valueGetter: ({ data }: { data: ManagerStat }) =>
                        data?.currentMonthDelivered || 0,
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: ManagerStat }) =>
                            data.currentMonthDelivered >= data.currentMonthOrdered &&
                            data.currentMonthDelivered >= data.planned,
                    },
                    minWidth: 70,
                    maxWidth: 70,
                },
                {
                    field: "currentMonthProcessed",
                    headerName: formatMessage({ id: "sites.grid.col.processed" }),
                    valueGetter: ({ data }: { data: ManagerStat }) =>
                        data?.currentMonthProcessed || 0,
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: any) =>
                            data.currentMonthProcessed >= data.currentMonthDelivered &&
                            data.currentMonthProcessed >= data.planned,
                    },
                    minWidth: 70,
                    maxWidth: 70,
                    cellStyle: { borderRight: "1px solid var(--ag-border-color)" },
                },
                {
                    field: "currentMonthWaiting",
                    headerName: formatMessage({ id: "sites.grid.col.waiting" }),
                    valueGetter: ({ data }: { data: ManagerStat }) =>
                        data?.currentMonthWaiting || 0,
                    cellClassRules: {
                        "order-success": ({ data }: any) =>
                            data.currentMonthWaiting >= data.currentMonthProcessed &&
                            data.currentMonthWaiting >= data.planned,
                    },
                    cellClass: "number-cell",
                    minWidth: 70,
                    maxWidth: 70,
                },
                {
                    field: "currentMonthPublished",
                    headerName: formatMessage({ id: "sites.grid.col.published" }),
                    valueGetter: ({ data }: { data: ManagerStat }) =>
                        data?.currentMonthPublished || 0,
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: ManagerStat }) =>
                            data.currentMonthPublished >= data.currentMonthOrdered &&
                            data.currentMonthPublished >= data.currentMonthDelivered &&
                            data.currentMonthPublished >= data.planned,
                    },
                    minWidth: 70,
                    maxWidth: 70,
                    cellStyle: { borderRight: "1px solid var(--ag-border-color)" },
                },
            ],
        },
        {
            headerName: formatMessage({ id: "sites.grid.col.nextMonth" }),
            children: [
                {
                    field: "nextMonthOrdered",
                    headerName: formatMessage({ id: "sites.grid.col.ordered" }),
                    valueGetter: ({ data }: { data: ManagerStat }) =>
                        `${data?.nextMonthOrdered || 0}`,
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: ManagerStat }) =>
                            data.nextMonthOrdered >= data.planned,
                    },
                    minWidth: 100,
                    maxWidth: 100,
                    cellStyle: { borderLeft: "1px solid var(--ag-border-color)" },
                },
                {
                    field: "nextMonthDelivered",
                    headerName: formatMessage({ id: "sites.grid.col.delivered" }),
                    valueGetter: ({ data }: { data: ManagerStat }) => data?.nextMonthDelivered || 0,
                    cellClass: "number-cell",
                    cellClassRules: {
                        "order-success": ({ data }: { data: ManagerStat }) =>
                            data.nextMonthDelivered >= data.nextMonthOrdered &&
                            data.nextMonthDelivered >= data.planned,
                    },
                    minWidth: 70,
                    maxWidth: 70,
                },
                {
                    field: "nextMonthProcessed",
                    headerName: formatMessage({ id: "sites.grid.col.processed" }),
                    valueGetter: ({ data }: { data: ManagerStat }) => data?.nextMonthProcessed || 0,
                    cellClassRules: {
                        "order-success": ({ data }: any) =>
                            data.nextMonthProcessed >= data.nextMonthDelivered &&
                            data.nextMonthProcessed >= data.planned,
                    },
                    cellClass: "number-cell",
                    minWidth: 70,
                    maxWidth: 70,
                },
                {
                    field: "nextMonthWaiting",
                    headerName: formatMessage({ id: "sites.grid.col.waiting" }),
                    valueGetter: ({ data }: { data: ManagerStat }) => data?.nextMonthWaiting || 0,
                    cellClassRules: {
                        "order-success": ({ data }: any) =>
                            data.nextMonthWaiting >= data.nextMonthProcessed &&
                            data.nextMonthWaiting >= data.planned,
                    },
                    cellClass: "number-cell",
                    minWidth: 70,
                    maxWidth: 70,
                },
            ],
        },
    ];

    return gridColumns;
}
